import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Seo from "../components/Seo";

type ProductScreenshot = {
  id: number;
  image: string;
  alt?: string;
};

// Product data - easily extensible for future products
const products = [
  {
    id: 1,
    name: "zero-harm-ai-detectors (Released)",
    description: "AI Detectors is a Python-based AI safety and privacy protection library designed to automatically detect and redact sensitive, risky, and harmful content in text. It combines modern transformer-based AI models with high-performance heuristic detectors to balance accuracy, speed, and deployability.",
    category: "AI + Security",
    technologies: ["Transformer-based NLP", "Heuristic"],
    link: "https://pypi.org/project/zero-harm-ai-detectors/",
    screenshots: [
      {
        id: 1,
        image: "/screenshots/ai-library-1.png",
        alt: "AI Safety Library dashboard showing PII detection and redaction results",
      },
    ],
  },
  {
    id: 2,
    name: "zero-harm-ai-gha (Released)",
    description: "zero-harm-ai-gha is a GitHub Action for AI security reviews in pull requests. It scans code changes for prompt injection risk, PII and secret exposure, and unsafe LLM usage patterns before code is merged.",
    category: "AI + Security",
    technologies: ["GitHub Actions", "PR Security", "LLM-Powered"],
    link: "https://github.com/Zero-Harm-AI/zero-harm-ai-gha",
    screenshots: [
      {
        id: 2,
        image: "/screenshots/codereviewer-1.png",
        alt: "zero-harm-ai-gha review output highlighting AI security findings in a pull request",
      },
    ],
  },
  {
    id: 3,
    name: "zero-harm-ai-mcp (Released)",
    description: "zero-harm-ai-mcp is an MCP server for AI safety and privacy workflows. It gives applications and agents access to detection and redaction tooling for sensitive, risky, and harmful content.",
    category: "AI + Security",
    technologies: ["MCP Server", "AI Safety", "Privacy Tooling"],
    link: "https://github.com/Zero-Harm-AI/zero-harm-ai-mcp",
    screenshots: [
      {
        id: 3,
        image: "/screenshots/zero-harm-ai-mcp-1.png",
        alt: "Diagram showing an app connecting through zero-harm-ai-mcp to tools, an LLM, and other AI agents",
      },
    ],
  },
];

// Rotating screenshot display for a product card
function ImageDisplay({ screenshots }: { screenshots: ProductScreenshot[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (screenshots.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % screenshots.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [screenshots.length]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      {screenshots.map((screenshot, index) => (
        <motion.div
          key={screenshot.id}
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: index === currentIndex ? 1 : 0 }}
          transition={{ duration: 0.5 }}
        >
          <img
            src={screenshot.image}
            alt={screenshot.alt || `Screenshot ${index + 1}`}
            className="w-full h-full object-cover"
          />
        </motion.div>
      ))}
    </div>
  );
}

// Product card component (scaled to 2/3)
function ProductCard({ product }: { product: typeof products[0] }) {
  const navigate = useNavigate();

  return (
    <div
      className="shrink-0 w-[267px] md:w-[333px] bg-white rounded-2xl shadow-xl overflow-hidden mx-3 flex flex-col hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
      style={{ height: "480px" }}
      onClick={() => navigate("/products")}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate("/products");
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Open ${product.name} in Products`}
    >
      <div className="bg-linear-to-br from-gray-50 to-gray-100 flex items-center justify-center w-full h-[55%]">
        <ImageDisplay screenshots={product.screenshots} />
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider mb-1">
          {product.category}
        </span>
        <h3 className="text-base font-bold text-gray-900 mb-1.5">{product.name}</h3>
        <p className="text-xs text-gray-600 mb-3 line-clamp-3 flex-1">{product.description}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {product.technologies.map((tech) => (
            <span
              key={tech}
              className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-[10px] rounded-full"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Portfolio section with conditional scroll hint
function PortfolioSection({ products: productList }: { products: Array<typeof products[0]> }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const container = scrollRef.current;
      if (container) {
        setHasOverflow(container.scrollWidth > container.clientWidth);
      }
    };

    checkOverflow();
    const timeoutId = setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
      clearTimeout(timeoutId);
    };
  }, [productList]);

  return (
    <>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-8 bg-linear-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-linear-to-l from-white to-transparent z-10 pointer-events-none" />
        <div
          ref={scrollRef}
          className="flex overflow-x-auto pb-4 pt-2 px-2 gap-0 snap-x snap-mandatory justify-center"
          style={{ scrollBehavior: "smooth", WebkitOverflowScrolling: "touch" }}
        >
          {productList.map((product) => (
            <div key={product.id} className="snap-start">
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      </div>
      {hasOverflow && (
        <p className="text-center text-sm text-gray-500 mt-4">
          ← Scroll to explore more tools →
        </p>
      )}
    </>
  );
}

export default function Home() {
  const navigate = useNavigate();

  return (
    <>
      <Seo
        title="Zero Harm AI | Agentic AI & AI Security Development"
        description="Zero Harm AI designs and builds AI agents and AI security solutions: agentic workflows, MCP integrations, guardrails, red teaming, and sensitive-data protection. Creator of the ZeroHarmAI open-source security toolkit."
        socialDescription="Zero Harm AI builds AI agents that automate real work, and the AI security that keeps them safe in production."
        path="/"
      />

      <div className="p-4 md:p-6">
        {/* Hero Section */}
        <section className="text-center mb-12 py-8 bg-linear-to-br from-blue-50 to-indigo-50 rounded-xl">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-4xl font-bold mb-3 bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"
          >
            Agentic AI &amp; AI Security Development
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-6"
          >
            We design and build AI agents that automate real work, and the AI security
            that keeps them safe in production.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.8 }}
            className="text-sm md:text-base italic text-indigo-700 font-medium mb-6"
          >
            Creator of the ZeroHarmAI open-source security toolkit
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-3"
          >
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              Build an AI Agent
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2.5 bg-white text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 font-semibold transition-all"
            >
              Secure Your AI
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2.5 bg-white text-indigo-600 border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 font-semibold transition-all"
            >
              Agent Security Assessment
            </button>
          </motion.div>
        </section>

        {/* Core Services */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">What We Build</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all"
            >
              <div className="text-4xl mb-3">🤖</div>
              <h3 className="text-xl font-bold mb-2 text-blue-600">AI Agent Development</h3>
              <p className="text-gray-600 mb-3 text-sm">
                Custom AI agents and multi-agent workflows with RAG, MCP and tool integrations, memory, and evaluations, built for production.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all"
            >
              <div className="text-4xl mb-3">🛡️</div>
              <h3 className="text-xl font-bold mb-2 text-indigo-600">AI Security Development</h3>
              <p className="text-gray-600 mb-3 text-sm">
                Guardrails, prompt-injection defenses, PII and secret redaction, red-team testing, and custom security tooling for LLM apps and agents.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all"
            >
              <div className="text-4xl mb-3">⚙️</div>
              <h3 className="text-xl font-bold mb-2 text-purple-600">Secure Agent Operations</h3>
              <p className="text-gray-600 mb-3 text-sm">
                Secure CI/CD, permissions, observability, monitoring, and governance so your agents stay reliable after launch.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Products / Portfolio Section */}
        <section className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-3 bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Our Portfolio
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              ZeroHarmAI open-source tools for sensitive-data protection, agent security, and secure AI development workflows.
            </p>
          </motion.div>
          <PortfolioSection products={products} />
        </section>

        {/* Who We Work With */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Who We Work With</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition">
              <div className="text-4xl mb-3">🚀</div>
              <h3 className="text-xl font-bold mb-2">Startups</h3>
              <p className="text-gray-600 text-sm">
                Build your first AI agents and AI-native products on a secure foundation.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition">
              <div className="text-4xl mb-3">📈</div>
              <h3 className="text-xl font-bold mb-2">Growing Companies</h3>
              <p className="text-gray-600 text-sm">
                Turn agent prototypes into reliable, secured production systems that automate real work.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition">
              <div className="text-4xl mb-3">🏢</div>
              <h3 className="text-xl font-bold mb-2">Enterprises</h3>
              <p className="text-gray-600 text-sm">
                Agent development, AI security controls, and platform integration for complex environments.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-blue-600 text-white p-10 rounded-xl">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready to Build AI Agents You Can Trust?</h2>
          <p className="text-base mb-6 max-w-2xl mx-auto">
            From first prototype to a secured production agent, we build it with you.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate("/contact")}
              className="px-6 py-2.5 bg-white text-blue-600 rounded-lg hover:bg-gray-100 font-semibold shadow-lg transition-all transform hover:scale-105"
            >
              Schedule Consultation
            </button>
            <button
              onClick={() => navigate("/about")}
              className="px-6 py-2.5 bg-transparent border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600 font-semibold transition-all"
            >
              Learn More About Us
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
